import { createRecipe, implementCustomExpressions } from '../__generated/implementation.js';
import { ApplianceWithPowderCoating, Handing, LynxProfessionalOutdoorBeverageDispenserW24 } from '../__generated/model/index.js';
import { applianceData, fuelTypeToSkuSuffix } from '../data/appliances.js';
import { MaterialsMap, StainlessMaterialProperties } from '../data/cabinets.js';
import { PricingType, getCurrentUserPrice } from '../data/pricing.js';
import { isDealer } from '../util/auth.js';
import { round } from '../util/round.js';
import { NO_VARIANT, adjustableDoorHandingOptions } from './common.js';
const getApplianceData = (s)=>{
    const data = applianceData[s.$dataKey];
    if (!data) {
        throw new Error(`No appliance data found for type: ${s.type} (${s.$dataKey})`);
    }
    return data;
};
const getAppliancePanelMaterialProperties = (enabled, color)=>{
    if (enabled) {
        return MaterialsMap[color] ?? StainlessMaterialProperties;
    }
    return StainlessMaterialProperties;
};
const towerKitOptions = [
    {
        label: 'Single Tap',
        recipes: [
            createRecipe({
                type: 'LynxProfessionalSingleTapTowerKit'
            })
        ]
    },
    {
        label: 'Double Tap',
        recipes: [
            createRecipe({
                type: 'LynxProfessionalDoubleTapTowerKit'
            })
        ]
    }
];
export const applianceExpressions = implementCustomExpressions({
    Appliance: {
        $name: (s)=>getApplianceData(s).name,
        $description: (s)=>getApplianceData(s).description,
        $fuelTypes: (s)=>getApplianceData(s).fuelTypes,
        $dataKey: (s)=>{
            const variant = 'variant' in s ? s.variant : NO_VARIANT;
            return `${s.type}_${variant}`;
        }
    },
    ApplianceWithPowderCoating: {
        $dealerPrice: (s, _state, ctx)=>{
            const price = getCurrentUserPrice(ctx.publishedData.prices?.[s.$priceSku], {
                userType: PricingType.Dealer,
                powderCoatType: s.$room.powderCoatType
            });
            const powderCoatingUpcharge = ApplianceWithPowderCoating.is(s) && s.powderCoating ? getApplianceData(s).powderCoatingUpcharge.dealer : 0;
            return price + powderCoatingUpcharge;
        },
        $retailPrice: (s, _state, ctx)=>{
            const price = getCurrentUserPrice(ctx.publishedData.prices?.[s.$priceSku], {
                userType: PricingType.Retail,
                powderCoatType: s.$room.powderCoatType
            });
            const powderCoatingUpcharge = ApplianceWithPowderCoating.is(s) && s.powderCoating ? getApplianceData(s).powderCoatingUpcharge.retail : 0;
            return price + powderCoatingUpcharge;
        },
        $controlPanelColor: (s)=>getAppliancePanelMaterialProperties(s.powderCoating, s.$room.cabinetColor).color,
        $controlPanelMetalness: (s)=>getAppliancePanelMaterialProperties(s.powderCoating, s.$room.cabinetColor).metalness,
        $controlPanelRoughness: (s)=>getAppliancePanelMaterialProperties(s.powderCoating, s.$room.cabinetColor).roughness,
        $controlPanelRoughnessMapUrl: (s)=>getAppliancePanelMaterialProperties(s.powderCoating, s.$room.cabinetColor).roughnessMapUrl,
        $controlPanelNormalMapUrl: (s)=>getAppliancePanelMaterialProperties(s.powderCoating, s.$room.cabinetColor).normalMapUrl
    },
    BottomAppliance: {
        $priceSku: (s)=>getApplianceData(s).sku
    },
    BottomCabinetAppliance: {
        $sku: (s)=>{
            const { fuelTypes, sku } = getApplianceData(s);
            const fuelTypeSupported = fuelTypes.includes(s.$room.fuelType);
            if (fuelTypeSupported) {
                return `${sku}${fuelTypeToSkuSuffix(s.$room.fuelType)}`;
            }
            // This should be an invalid state and it is handled by an invariant,
            // but it should fail to be able to show FixDialog.
            return sku;
        },
        $applianceOffsetX: (s)=>s.$cabinet.$netWidth / 2,
        $applianceOffsetY: (s)=>{
            const offsetYFn = getApplianceData(s).offsetYFn;
            if (offsetYFn) {
                return offsetYFn(s);
            }
            return getApplianceData(s).offsetY;
        },
        $applianceOffsetZ: (s)=>getApplianceData(s).offsetZ
    },
    BottomWallAppliance: {
        $width: (s)=>round(s.$netWidth + s.$room.$cabinetSideGap, 4),
        $thumbnailUrl: (s)=>getApplianceData(s).thumbnailUrl,
        $height: (s)=>getApplianceData(s).height ?? 0,
        $hasWorkSurface: (s)=>{
            // Has tap on top, so it can't be used as a work surface.
            if (LynxProfessionalOutdoorBeverageDispenserW24.is(s)) {
                return false;
            }
            return true;
        }
    },
    BottomAdjustableDoorAppliance: {
        $sku: (s)=>`${getApplianceData(s).sku}-${s.handing === Handing.left ? 'LH' : 'RH'}`,
        $handingOptions: ()=>adjustableDoorHandingOptions,
        $offsetX: (s)=>s.handing === Handing.left ? s.$netWidth : 0,
        $offsetY: (s)=>getApplianceData(s).offsetY
    },
    BottomNonAdjustableDoorAppliance: {
        $sku: (s)=>getApplianceData(s).sku
    },
    LynxSink: {
        $width: (s)=>s.width
    },
    LynxProfessionalOutdoorIceMachineW15: {
        $summary: (s, _state, ctx)=>{
            const publishedProductData = ctx.publishedData.prices?.[s.$priceSku];
            if (!publishedProductData) {
                throw new Error(`No published product data found for sku: ${s.$priceSku}`);
            }
            return [
                {
                    id: s.id,
                    name: s.$name,
                    sku: s.$sku,
                    dealerPrice: s.$dealerPrice,
                    retailPrice: s.$retailPrice,
                    currentUserPrice: isDealer(ctx.sessionData) ? s.$dealerPrice : s.$retailPrice,
                    packageDepth: publishedProductData.packageDepth,
                    packageHeight: publishedProductData.packageHeight,
                    packageWidth: publishedProductData.packageWidth,
                    packageWeight: publishedProductData.packageWeight
                },
                ...s.$drainPump.$summary
            ];
        }
    },
    LynxProfessionalOutdoorBeverageDispenserW24: {
        $summary: (s, _state, ctx)=>{
            const publishedProductData = ctx.publishedData.prices?.[s.$priceSku];
            if (!publishedProductData) {
                throw new Error(`No published product data found for sku: ${s.$priceSku}`);
            }
            return [
                {
                    id: s.id,
                    name: s.$name,
                    sku: s.$sku,
                    dealerPrice: s.$dealerPrice,
                    retailPrice: s.$retailPrice,
                    currentUserPrice: isDealer(ctx.sessionData) ? s.$dealerPrice : s.$retailPrice,
                    packageDepth: publishedProductData.packageDepth,
                    packageHeight: publishedProductData.packageHeight,
                    packageWidth: publishedProductData.packageWidth,
                    packageWeight: publishedProductData.packageWeight
                },
                ...s.$towerKit.$summary
            ];
        },
        $towerKitOptions: ()=>towerKitOptions
    },
    LynxProfessionalTapTowerKit: {
        $offsetY: (s)=>s.$dispenser.$offsetY
    },
    CountertopAppliance: {
        $priceSku: (s)=>getApplianceData(s).sku,
        $sku: (s)=>getApplianceData(s).sku,
        $thumbnailUrl: (s)=>getApplianceData(s).thumbnailUrl,
        $width: (s)=>s.$netWidth,
        $height: (s)=>getApplianceData(s).height ?? 0
    },
    TopAppliance: {
        $priceSku: (s)=>getApplianceData(s).sku,
        $sku: (s)=>getApplianceData(s).sku,
        $thumbnailUrl: (s)=>getApplianceData(s).thumbnailUrl,
        $width: (s)=>s.$netWidth
    }
});

//# sourceMappingURL=appliance.js.map